if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
          document.activeElement.blur();
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        // In-memory variant section cache populated by
        // assets/product-variant-section-preload.js during idle. Shopify's
        // section responses carry Cache-Control: private, no-store (Set-Cookie
        // forces it), so the browser HTTP cache can't hold them — we keep
        // our own. Cache hit makes variant switching effectively instant
        // (Map.get + DOMParser, ~5ms total instead of a 150-300ms network
        // roundtrip).
        const cached = window.__variantSectionCache?.get?.(requestUrl);
        if (cached) {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(cached, 'text/html');
          callback(html);
          return;
        }

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            // Populate the cache for any subsequent re-click of the same
            // variant (e.g. customer toggles back). Same Map the preload
            // writes to.
            if (
              window.__variantSectionCache &&
              responseText &&
              responseText.length > 100 &&
              responseText.includes('<product-info')
            ) {
              window.__variantSectionCache.set(requestUrl, responseText);
            }
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            // document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        // Surgical update instead of full <variant-selects> DOM swap.
        //
        // The radio cards / dropdown options themselves are invariant
        // between variant selections within a single product — each card
        // already shows its own variant's data regardless of which
        // variant is currently selected. Swapping the entire
        // variant-selects element rebuilds ~30 nodes and triggers
        // Appstle's mutation observer to re-inject its widget DOM. Both
        // read as a visible flash on the bottom of the PDP every time
        // the user clicks a different variant. Refresh only the bits
        // that actually change.
        const newSelects = html.querySelector('variant-selects');
        const currentSelects = this.variantSelectors;
        if (!newSelects || !currentSelects) return;

        // 1. Selected-variant JSON — read by subsequent variant changes
        //    via getSelectedVariant().
        const newJson = newSelects.querySelector('[data-selected-variant]');
        const oldJson = currentSelects.querySelector('[data-selected-variant]');
        if (newJson && oldJson) oldJson.textContent = newJson.textContent;

        // 2. Visible "Selected: X" labels (e.g. "Color: Red") rendered
        //    in fieldset legends for swatch pickers.
        const newLabels = newSelects.querySelectorAll('[data-selected-value]');
        currentSelects.querySelectorAll('[data-selected-value]').forEach((dst, i) => {
          if (newLabels[i]) dst.textContent = newLabels[i].textContent;
        });

        // 3. Disabled state on each radio. Only changes between
        //    selections for multi-option products where one option's
        //    choice can disable another option's values. The clicked
        //    radio's `.checked` state is already correct via the
        //    browser's native handling — no update needed for that.
        currentSelects.querySelectorAll('input[type="radio"]').forEach((dst) => {
          if (!dst.id) return;
          const src = newSelects.querySelector(`input[id="${dst.id}"]`);
          if (!src) return;
          dst.classList.toggle('disabled', src.classList.contains('disabled'));
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          // ---------- CRITICAL PATH (sync, minimal) ----------
          // Only what the customer's eye / next interaction actually depends on:
          //   1. URL (back-button + share-link correctness)
          //   2. variant id in form (ATC reads from here)
          //   3. variant image (visible confirmation that variant changed)
          //   4. submit button state (out-of-stock handling)
          //
          // Everything else is deferred:
          //   - updateOptionValues: the radio's :checked state is already correct
          //     from the user's click. Only matters for programmatic variant
          //     changes (e.g. deep links).
          //   - main #price innerHTML: variant-picker-v2.js has ALREADY updated
          //     the per-pill subscription and one-time prices client-side from
          //     pre-loaded JSON (synchronously, before the fetch even started).
          //     Refreshing the main #price block also drives the subscription
          //     savings % update via global.js's MutationObserver on the
          //     [discount] token — that update now lags ≤200ms behind the image.
          //   - SKU / Inventory / Volume / Price-Per-Item: secondary text fields.
          //   - quantity rules / pickup widget: not visible during a click.
          //   - variantChange publish: drives price-per-item + recipient-form
          //     subscribers; both are cosmetic delays.
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          // ---------- DEFERRED (requestIdleCallback) ----------
          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          // The token guard makes rapid variant clicks safe: if the user clicks a
          // newer variant before the previous rIC fires, the older deferred work
          // bails so we don't update fields for a stale variant.
          const myToken = (this._variantUpdateToken = (this._variantUpdateToken || 0) + 1);
          const deferredWork = () => {
            if (myToken !== this._variantUpdateToken) return;
            this.updateOptionValues(html);
            this.pickupAvailability?.update(variant);
            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          };

          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(deferredWork, { timeout: 200 });
          } else {
            setTimeout(deferredWork, 0);
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // The FrontrowMD sticker sits in the first slide, usually the pack
        // shot this update removes: hold on to it (see keepFrontrowStickerFirst).
        const frontrowSticker = this.querySelector('#frontrow-sticker');
        const frontrowStickerWrap = frontrowSticker && frontrowSticker.parentElement;

        // Gallery A/B test (TASKS.md #149): on phones the challenger hides the
        // pack shot a shopper lands on (data-gallery-landing). A pack shot
        // chosen with the quantity picker shows in either arm, so the mark goes
        // from the fetched markup and from what is already on the page.
        [html, this, this.productModal].forEach((scope) => {
          scope?.querySelectorAll('[data-gallery-landing]').forEach((el) => el.removeAttribute('data-gallery-landing'));
        });

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // FAST PATH: when variants share the gallery (e.g. 1-jar / 2-jar / 3-jar
        // of the same product show the same product photos in the same order),
        // skip the 3-loop reconcile + the modal innerHTML rewrite entirely —
        // only call setActiveMedia below. This is the common JiYu PDP case and
        // it drops updateMedia from ~30-60ms to ~1-2ms.
        //
        // SLOW PATH stays in place for products whose variants legitimately
        // have different media (e.g. color variants with distinct photos).
        let galleryMatches = false;
        if (mediaGallerySource && mediaGalleryDestination) {
          const sourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const destItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          galleryMatches =
            sourceItems.length === destItems.length &&
            sourceItems.every((li, i) => li.dataset.mediaId === destItems[i].dataset.mediaId);
        }

        if (!galleryMatches && mediaGallerySource && mediaGalleryDestination) {
          const refreshSourceData = () => {
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
            );
            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };

          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Always: set the featured media as active in the gallery. Cheap (~1ms)
        // — just toggles the .is-active class and optionally moves the active
        // <li> to first. Bound to setActiveMedia in assets/media-gallery.js.
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // Modal update: only when gallery actually changed. When variants share
        // the gallery the modal content is identical, so the innerHTML rewrite
        // is wasted (and forces a sync style/layout recalc).
        if (!galleryMatches) {
          const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }

        this.keepFrontrowStickerFirst(frontrowStickerWrap);
      }

      // The FrontrowMD sticker (snippets/frontrow-banner.liquid) belongs on the
      // first slide on show. A pack shot this update added brings its own copy
      // of the sticker's markup, whose loader doesn't run when inserted, so it
      // would stand in the slide as an empty 300x150 frame pushing the image
      // down: drop it, and move the live sticker in. The frame reloads when
      // moved.
      keepFrontrowStickerFirst(stickerWrap) {
        const list = this.querySelector('media-gallery .product__media-list');
        if (!list) return;
        list.querySelectorAll('iframe.iframe-frontrow-sticker:not(#frontrow-sticker)').forEach((frame) => {
          const holder = frame.closest('#frontrow-banner-wrapper') || frame;
          if (holder !== stickerWrap) holder.remove();
        });
        if (!stickerWrap) return;
        const first = Array.from(list.children).find((slide) => slide.getClientRects().length);
        if (!first || first.contains(stickerWrap)) return;
        const host = first.querySelector('.product__modal-opener') || first;
        host.insertBefore(stickerWrap, host.firstChild);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
